// @refresh reset
// ↑ Dev-only React Fast Refresh directive (@vitejs/plugin-react). The board's
// nodes are built in useMemo([]), which Fast Refresh preserves across edits —
// so a content change (e.g. adding/removing a frame) wouldn't show without a
// manual reload. This forces the component to REMOUNT on every edit to this
// file, so useMemo re-runs and board edits hot-reload live. No prod effect
// (Fast Refresh is stripped from production builds).
import { useMemo, type ReactNode, type ComponentType } from "react";
import { type Node, type NodeTypes } from "@xyflow/react";
import { BoardCanvas } from "./BoardCanvas";
import { SectionBox, SectionGrid } from "./SectionBox";
import { DocumentFrame } from "./DocumentFrame";
import { nodeTypes as MKT_NODE_TYPES } from "./marketing/nodes";
import { FRAME_W } from "./marketing/theme";
import { band1Strategy } from "./marketing/bands/band1-strategy";
import { band2Website } from "./marketing/bands/band2-website";
import { band4OnePage } from "./marketing/bands/band4-onepage-plan";

// ─────────────────────────────────────────────────────────────────────────────
// MARKETING BOARD — refactored to the LOCKED board-container standard
// (Jarvis, 2026-07-19). The whole board is now ONE React Flow node: a
// <SectionGrid> that packs every section into a compact rectangle. Each band /
// zone is ONE <SectionBox> (neutral-gray box, big black label INSIDE at the
// top); the real content lives as WHITE cards within, flowing into an internal
// wrap-grid so no section is tall. The old floating sectionTitle / title /
// band-title nodes are dropped — the SectionBox label is the only label.
//
// The marketing content components + data are UNCHANGED: the band factories
// still emit { type, data } nodes; here we drop their positions, drop the
// chrome (sectionTitle/title) nodes, regroup the content into SectionBoxes, and
// render each item through the existing marketing node component registry.
// ─────────────────────────────────────────────────────────────────────────────

const T = { bg: "#E5E5E5", dots: "#CFCFCF" };

// The marketing node components all render standalone from a `{ data }` prop
// (they are plain function components; MapCard's hidden Handles stay inert since
// this board carries no edges). Reuse them as a type→component registry.
const REG = MKT_NODE_TYPES as unknown as Record<string, ComponentType<{ data: any }>>;

type SecItem = { type: string; data: any };

function renderSecItem(it: SecItem, key: number): ReactNode {
  // a plain in-container gap (keeps a group visually apart inside one section)
  if (it.type === "spacer") return <div key={key} style={{ width: it.data?.w ?? 100, flexShrink: 0 }} />;
  const Comp = REG[it.type];
  return Comp ? <Comp key={key} data={it.data} /> : null;
}

// A knowledge-base page rendered ON the board: fixed height, scrolls in place,
// double-click to zoom (BoardCanvas `data-zoom`). Erez, 2026-07-25: "if they're
// not on the board, they don't exist for me" — the strategy documents live in
// page_content and are READ here, rather than being flattened into card walls.
type DocRef = { slug: string; label: string };
const DOC_W = 820;
const DOC_H = 1060;

// One board section = one SectionBox. Its content wraps into an internal grid
// (flex-wrap capped at maxW) so many items read as a wide rectangle, never a
// tall column. A section carries EITHER board-native items OR documents.
function Section({ title, items, docs, maxW, gap = 80 }: { title: string; items?: SecItem[]; docs?: DocRef[]; maxW: number; gap?: number }) {
  const hasItems = !!items?.length;
  const hasDocs = !!docs?.length;
  if (!hasItems && !hasDocs) return null;
  return (
    <SectionBox key={title} title={title} layout="custom">
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap, maxWidth: maxW }}>
        {hasDocs
          ? docs!.map((d) => <DocumentFrame key={d.slug} slug={d.slug} label={d.label} width={DOC_W} height={DOC_H} />)
          : items!.map((it, i) => renderSecItem(it, i))}
      </div>
    </SectionBox>
  );
}

// ── content selectors — pick content nodes from a band, drop chrome + position ─
const toItems = (nodes: Node[], pred: (n: Node) => boolean): SecItem[] =>
  nodes.filter(pred).map((n) => ({ type: String(n.type), data: n.data as any }));
const byId = (...ids: string[]) => (n: Node) => ids.includes(String(n.id));
const byType = (...t: string[]) => (n: Node) => t.includes(String(n.type));
const idStarts = (...p: string[]) => (n: Node) => p.some((x) => String(n.id).startsWith(x));
const and = (a: (n: Node) => boolean, b: (n: Node) => boolean) => (n: Node) => a(n) && b(n);

// LinkedIn Launch band (Leo) — the controlled go-live plan for switching Erez's
// profile to Founder & CEO of Tailormind. Content nodes only (headers dropped).
function linkedinLaunchBand(): Node[] {
  return [
    {
      id: "li-switch", type: "stratCard", position: { x: 0, y: 0 },
      data: {
        label: "How it triggers",
        title: "One master switch decides what your network sees",
        lede: "LinkedIn has a single setting — Settings › Visibility › \"Share profile updates with your network.\" With it OFF, you can rebuild the whole profile in the dark. Nobody is notified. The launch is a moment we choose, not a leak.",
        bullets: [
          "Silent (no notification): the Company Page, and every profile edit while the switch is OFF",
          "Triggers your network: an announcement POST, and edits made while sharing is ON",
          "So: build everything dark, review together, then flip the switch and announce on purpose",
        ],
      },
      draggable: false, selectable: false,
    },
    {
      id: "li-timeline", type: "timeline", position: { x: 0, y: 0 },
      data: {
        phases: [
          {
            when: "Silent prep", title: "Build it dark (silent)", items: [
              "Settings › Visibility: turn OFF \"Share profile updates\"",
              "Create the Tailormind Company Page (tree logo, tagline, tailormind.co)",
              "Stage new banner + new photo",
              "Headline → Founder & CEO, Tailormind",
              "About → one plain line on what you do now",
              "Featured → pin tailormind.co",
            ],
          },
          {
            when: "Lock", title: "Review together (silent)", items: [
              "Add \"Founder & CEO · Tailormind\" linked to the page — do NOT tick \"notify network\"",
              "Set \"CTO · Glassworks\" to a past role (add end date)",
              "Read the whole profile top-to-bottom, together",
              "Fix anything before a single person sees it",
            ],
          },
          {
            when: "Go live", title: "Announce (triggers)", items: [
              "Turn \"Share profile updates\" back ON",
              "Publish one short launch post to the 500+ network",
              "That post is the trigger — not the silent edits",
              "Pin the post + tailormind.co to Featured",
            ],
          },
        ],
      },
      draggable: false, selectable: false,
    },
    {
      id: "li-photo", type: "stockCandidate", position: { x: 0, y: 0 },
      data: {
        label: "Profile photo · Options 1–4 · circle-cropped, LinkedIn-style — pick one",
        img: "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/assets/9603688b-d23f-45f1-a55b-6c7c8cfe253e-tailormind-photo-options.png",
        w: 2000,
      },
      draggable: false, selectable: false,
    },
    {
      id: "li-cover", type: "stockCandidate", position: { x: 0, y: 0 },
      data: {
        label: "Cover banner · Options 1–3 · 4:1 LinkedIn cover crops — pick one",
        img: "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/assets/8f7d19e3-1189-4167-bcf9-ecb3906617ba-tailormind-cover-options.png",
        w: 2000,
      },
      draggable: false, selectable: false,
    },
    {
      id: "li-finalphoto", type: "stockCandidate", position: { x: 0, y: 0 },
      data: {
        label: "Profile photo · Option 2 — the other person cut out and filled with sky, exact same framing",
        img: "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/assets/af97aa43-cc28-4c98-9f5d-a3b064342512-tailormind-option2-sideremoved.png",
        w: 900,
      },
      draggable: false, selectable: false,
    },
  ];
}

// Nav mega-menu reference (Leo) — Duna's mega-menus vs OUR Use Cases mega-menu.
function navMegaRefBand(): Node[] {
  const R2 = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/assets/nav-ref-v2";
  const W = 900;
  const frame = (id: string, label: string, tag: string, url: string): Node => ({
    id, type: "siteFrame", position: { x: 0, y: 0 },
    data: { label, tag, img: url, w: W },
    draggable: false, selectable: false,
  });
  return [
    frame("navref-duna-navbar",    "Duna — nav bar",             "REFERENCE", `${R2}/f57c3325-3efb-471c-9b0c-040d70b706bf-duna-navbar.jpg`),
    frame("navref-duna-product",   "Duna — Product mega-menu",   "REFERENCE", `${R2}/d467aead-bc2d-4c84-a3b4-bd23ec843023-duna-product.jpg`),
    frame("navref-duna-customers", "Duna — Customers mega-menu", "REFERENCE", `${R2}/90394c20-ecbe-4ffc-b593-21077c5dcf2c-duna-customers.jpg`),
    frame("navref-duna-company",   "Duna — Company mega-menu",   "REFERENCE", `${R2}/12d564b2-bcaf-4095-8f97-a795605ad6a0-duna-company.jpg`),
    frame("navref-ours",           "Ours — Use Cases mega-menu", "OURS",      `${R2}/ec4fa253-0e79-478e-9018-c863b4a8cea9-ours-usecases.jpg`),
  ];
}

// Website Playground (Leo) — 4 LONGER page-layout OPTIONS per service, each
// assembled from our prop-driven block kit + copy bank (marketing-content law).
// Erez compares the four per service and picks one winner, which then graduates
// into the real /service/{svc} page. Live on preview-nav-v2. Frames grouped by
// service so each service reads as its own cluster.
function playgroundBand(bust: string): Node[] {
  const R2 = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/board/marketing";
  const TPL = ["Editorial", "Visual", "Method", "Outcome"];
  const out: Node[] = [];
  const frame = (key: string, n: number, label: string): Node => ({
    id: `pg-${key}-${n}`, type: "siteFrame", position: { x: 0, y: 0 },
    data: {
      label,
      tag: `/service/${key}/opt-${n}`,
      img: `${R2}/pg-${key}-opt-${n}.jpg${bust}`,
      w: FRAME_W, // one standard frame width across the whole board
    },
    draggable: false, selectable: false,
  });
  // Discovery — the finalized page (opt-1). It lives in the Website section now,
  // not the Playground; kept here as the single frame Website references.
  out.push(frame("discovery", 1, "Discovery — our page"));
  // Build / Run / Enablement — the original four options each.
  ["build", "run", "enablement"].forEach((key) => {
    for (let n = 1; n <= 4; n++) out.push(frame(key, n, `Option ${n} · ${TPL[n - 1]}`));
  });
  return out;
}

type RowSec = { title: string; items?: SecItem[]; docs?: DocRef[]; maxW: number };

// ── the whole board: one SectionGrid of SectionBoxes, grouped into explicit ROWS.
// Each inner array is one row; a full-width zero-height flex break forces the next
// row onto its own line, so the board reads as clean stacked rows regardless of the
// sections' natural widths (Erez-locked Marketing row layout, 2026-07-19):
//   Row 1 · Strategy & Research   Row 2 · Website (merged)   Row 3 · Nav · Plan · LinkedIn
function GridNode({ data }: { data: { rows: RowSec[][] } }) {
  const kids: ReactNode[] = [];
  data.rows.forEach((row, ri) => {
    row.forEach((s) => kids.push(<Section key={s.title} title={s.title} items={s.items} docs={s.docs} maxW={s.maxW} />));
    if (ri < data.rows.length - 1) kids.push(<div key={`brk-${ri}`} style={{ flexBasis: "100%", height: 0 }} />);
  });
  // Widened past the 24000 default so Website + the 4 standard-width Playground
  // clusters all fit on one row (other rows just left-pack, unaffected).
  return <SectionGrid width={30000}>{kids}</SectionGrid>;
}

const nodeTypes: NodeTypes = { grid: GridNode as unknown as NodeTypes[string] };

function buildRows(): RowSec[][] {
  // Static version tag, NOT Date.now(): a fresh timestamp every mount defeated the
  // browser cache entirely, forcing a 20-30MB re-download of the full-page frames
  // on every board open. Bump this string only when the underlying captures change.
  const bust = `?v=2026-07-24o`;
  const b1 = band1Strategy();
  const b2 = band2Website(bust).filter((n) => !String(n.id).startsWith("td-")); // teardowns omitted
  const b4 = band4OnePage();
  const li = linkedinLaunchBand();
  const nav = navMegaRefBand();
  const pg = playgroundBand(bust);

  // Row 2 · "Website" = the two Duna references, our homepage, the finalized
  // Discovery page, then the two mobile frames. (Old homepage ours-v2 removed;
  // the 4 svc-page frames removed — Build/Run/Enablement stay in Playground.)
  const websiteItems: SecItem[] = [
    ...toItems(b2, and(byType("siteFrame"), byId("ours"))),       // homepage — desktop
    ...toItems(b2, and(byType("siteFrame"), byId("mob-ours"))),   // homepage — mobile
    { type: "spacer", data: { w: 160 } },                        // a little space, same container
    ...toItems(pg, byId("pg-discovery-1")),                       // services — Discovery
    { type: "spacer", data: { w: 160 } },
    ...toItems(b1, byType("wireframe")),                          // homepage wireframe — moved down from row 1 (it is an image, it belongs with the frames)
  ];

  return [
    // ── ROW 1 · The knowledge ─────────────────────────────────────────────────
    // Erez-locked 2026-07-25. Row 1 used to be SIX card-wall sections (Strategy,
    // Content map, Pain pages, Homepage, Competitors, SEO & GEO) holding ~50
    // units of PROSE crammed into boxes. Prose is READ, so it is a DOCUMENT:
    // every one of those units now lives in page_content and is rendered here by
    // <DocumentFrame> — fixed height, scrolls in place, double-click to zoom.
    // The band1 nodes they replaced were deleted in the same commit.
    // Two things did NOT become documents: the loop strip (live state, you SCAN
    // it) stays board-native, and the homepage wireframe (an image) moved down
    // into Row 2 where the rest of the site frames live.
    [
      { title: "The loop", maxW: 900, items: toItems(b1, byId("proc-strip")) },
      { title: "Strategy", maxW: 3560, docs: [
        { slug: "kb-doc/consultancy/marketing/strategy/the-strategy", label: "The Strategy — positioning · ICP · goals · roadmap" },
        { slug: "kb-doc/consultancy/marketing/competitors/the-analysis", label: "Competitors — 45 companies, verbatim" },
        { slug: "kb-doc/consultancy/marketing/seo-geo", label: "SEO & GEO playbook" },
        { slug: "kb-doc/consultancy/marketing/content-framework", label: "The Content Framework" },
      ] },
      { title: "Execution", maxW: 4460, docs: [
        { slug: "kb-doc/consultancy/marketing/site-and-pages", label: "Site plan & page specs — locked homepage copy" },
        { slug: "kb-doc/consultancy/marketing/case-studies-deep-dive", label: "Case studies & proof" },
        { slug: "kb-doc/consultancy/marketing/strategy/lead-engine", label: "Lead engine & build order" },
        { slug: "kb-doc/consultancy/marketing/strategy/the-numbers", label: "The numbers — revenue & forecast" },
        { slug: "kb-doc/consultancy/marketing/marketing-os-blueprint", label: "Marketing OS — how we run it" },
      ] },
    ],
    // ── ROW 2 · Website + Playground — same row (website frames, then the 4
    //    service-option clusters to the right, so Erez compares them beside the site) ──
    [
      { title: "Website", maxW: 3600, items: websiteItems },
      // Each cluster = its 4 options in ONE row (4 across) at the STANDARD frame
      // width (FRAME_W). Discovery is finalized (moved to Website); Build/Run/
      // Enablement stay here as WIP.
      { title: "Playground · Build", maxW: 4000, items: toItems(pg, idStarts("pg-build-")) },
      { title: "Playground · Run", maxW: 4000, items: toItems(pg, idStarts("pg-run-")) },
      { title: "Playground · Enablement", maxW: 4000, items: toItems(pg, idStarts("pg-enablement-")) },
    ],
    // ── ROW 3 · Nav menu · 1-Page Plan · LinkedIn ──
    [
      { title: "Nav menu", maxW: 1950,
        items: toItems(nav, byType("siteFrame")) },
      { title: "1-Page Plan", maxW: 2500,
        items: toItems(b4, byType("section", "stratCard")) },
      { title: "LinkedIn", maxW: 2100,
        items: toItems(li, byType("stratCard", "timeline", "stockCandidate")) },
      { title: "Duna (reference)", maxW: 3400,
        items: toItems(b2, and(byType("siteFrame"), byId("duna-productai", "duna", "mob-duna"))) },
    ],
  ];
}

export function MarketingBoardPage() {
  const nodes = useMemo<Node[]>(() => {
    const rows = buildRows();
    return [{ id: "board-grid", type: "grid", position: { x: 0, y: 0 }, draggable: false, data: { rows } }];
  }, []);

  return (
    <div className="mkt-board" style={{ position: "absolute", inset: 0 }}>
      {/* image cards never intercept the pan/drag gesture */}
      <style>{`.mkt-board .react-flow__node img { pointer-events: none; }`}</style>
      <BoardCanvas nodes={nodes} nodeTypes={nodeTypes} fitViewPadding={0.12} />
    </div>
  );
}

export default MarketingBoardPage;
